import React, { FunctionComponent } from 'react';
import { Badge, Checkbox, Tooltip } from '../../tailwindcomponents';
import { Person, ShoppingCart, PriorityHigh, LocalAirport, HourglassFull } from '../icons';
import { NotificationsActive as NotificationsActiveIcon } from '../icons';
import { Event, PlannedTask } from '../../types/tasks';
import { formatDueDate } from '../../utils/dateUtils';
import { palette } from '../../theme/palette';

type CalendarTaskProps = {
    isTaskAssignment?: boolean,
    edit: Boolean,
    onCheckboxClicked: Function
    event: Event
    checked: boolean
    clickedTaskDate: PlannedTask | undefined,
    isLinePlanner: boolean | undefined
}

const CalendarTask: FunctionComponent<CalendarTaskProps> = React.memo(({ edit = true, onCheckboxClicked, event, checked, clickedTaskDate, isLinePlanner, isTaskAssignment }) => {
    const taskDetails = event.task_details;
    const assignedEmployees = taskDetails.assigned_employees_on_calendar;
    const status = taskDetails?.status;

    // Prevent open task dialog from opening when clicking checkbox
    const onTaskChecked = (mouseEvent: React.MouseEvent) => {
        mouseEvent.stopPropagation();
        onCheckboxClicked(event, !checked);
    }
    
    const backgroundColor = event.task_details.color;
    const textColor = event.task_details.color === palette.backgroundPaper ? palette.content : palette.backgroundPaper; //changing the text color to black for tasks with a  bone-white background.

    let taskPriority = "";
    if (event.task_priority === "P01") {
      taskPriority = "P1";
    }
    else if (event.task_priority === "P02") {
      taskPriority = "P2";
    }
    else if (event.task_priority === "P03") {
      taskPriority = "P3";
    }

    return (
        <div id={"calendar-task-" + event.scheduled_task_id} className={clickedTaskDate !== undefined && clickedTaskDate.scheduled_task_id === event.scheduled_task_id ? 'border-[5px] border-solid border-black m-[0.5em]' : ''}>
            <div
                className={`rounded-[15px] border-t-[ridge] min-h-[50px] max-h-[80px] ${
                    event.task_details.color ? 'border-accent-blue'
                    : status === "ACTV" ? 'bg-status-actv border-accent-blue'
                    : status === "COMPLETE" ? 'bg-status-complete border-status-complete'
                    : status === "PAUSE" ? 'bg-status-pause border-status-pause text-black/[0.87]'
                    : status === "IN WORK" ? 'bg-accent-blue border-accent-blue'
                    : status === "CANCEL" ? 'bg-status-cancel border-status-cancel'
                    : status === "COMMIT" ? 'bg-status-actv border-accent-blue'
                    : status === "HEADER" ? 'bg-black'
                    : ''
                }`}
                style={event.task_details.color ? {
                    backgroundColor: backgroundColor || palette.status.actv,
                    color: textColor || 'rgba(0, 0, 0, 0.87)',
                } : undefined}
            >
                {edit &&
                    <Checkbox className="float-left mt-[-9px] ml-[5px] mr-[-5px] mb-0 relative text-inherit" checked={checked} color="default" onMouseDown={(e) => { e.stopPropagation() }} onClick={(e) => { onTaskChecked(e) }} />
                }
                {assignedEmployees !== null && assignedEmployees.length > 0 &&
                    <Tooltip title={assignedEmployees} type='primary' placement="top-start"><Person className="float-left mt-[5px] ml-[8px] mb-0 relative" /></Tooltip>
                }
                {event.task_details.part_amounts > 0 &&
                    <Tooltip title={`${event.task_details.part_amounts} parts`} type='primary' placement="top-start">
                        <Badge badgeContent={event.task_details.part_amounts} color="primary" className="float-left mt-[5px] ml-[8px] mb-0 relative" >
                            <ShoppingCart color={event.task_details.part_not_available ? 'primary' : 'error'} />
                        </Badge>
                    </Tooltip>
                }
                {taskDetails.is_messages &&
                    <Tooltip title={'Message'} type='primary' placement="top-start"><NotificationsActiveIcon color={taskDetails.is_message_unseen ? 'error' : 'inherit'} className="float-left mt-[5px] ml-[8px] mb-0 relative" /></Tooltip>
                }
                {taskDetails.is_task_critical &&
                    <Tooltip title={'Critical Work'} type='primary' placement="top-start"><PriorityHigh color={taskDetails.is_task_critical ? 'error' : 'inherit'} className="float-left mt-[5px] ml-[8px] mb-0 relative" /></Tooltip>
                }
                {taskDetails.is_on_hold &&
                    <Tooltip title={'On hold'} type='primary' placement="top-start"><HourglassFull color={taskDetails.status === "CANCEL" ? 'inherit' : 'error'} className="float-left mt-[5px] ml-[8px] mb-0 relative" /></Tooltip>
                }
                {taskDetails.is_on_time === false &&
                    <Tooltip title={'Aircraft is not on time, Reschedule'} type='primary' placement="top-start"><LocalAirport color={taskDetails.status === "CANCEL" ? 'inherit' : 'error'} className="float-left mt-[5px] ml-[8px] mb-0 relative" /></Tooltip>
                }
                {isTaskAssignment &&
                    <p className="m-0 mr-[5px] text-center text-ellipsis">
                        {taskDetails.wp_line_no !== undefined &&
                            <span className="text-[12px] text-center">
                                Task No: {taskDetails.wp_line_no}
                            </span>}
                    </p>
                }
                {taskPriority !== "" &&
                <div className="w-[25px] h-[25px] bg-red-600 text-white font-bold text-[16px] flex items-center justify-center rounded-full text-center shrink-0 mt-[5px] ml-[8px]">
                    {taskPriority}
                </div>
                }
                {isTaskAssignment &&
                    <p className="m-0 mr-[5px] text-center text-ellipsis">
                        {taskDetails.wp_line_no !== undefined &&
                            <span className="text-[12px] text-center">
                                Status: {taskDetails.status}
                            </span>}
                    </p>
                }
                <p className="m-0 mr-[5px] text-center text-ellipsis">
                    {taskDetails.mxi_task_id !== 0 &&
                        <span className="text-[12px] text-center">
                            #{taskDetails.mxi_task_id}
                        </span>}
                    <Tooltip title={`Due: ${formatDueDate(event.due_date)}`} type='primary' placement='top-end'>
                        {event.title}
                    </Tooltip>
                </p>
            </div>
        </div>
    );
});
export default CalendarTask;
